import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Plus, Search, FileInput, Eye, Pencil, Trash2, Copy, 
  Check, Upload, X, Download,
  ChevronUp, ChevronDown, Filter, RotateCcw, Split, Settings,
  GripVertical, EyeOff, Paperclip, File, FileText, Image
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Combobox } from '../components/ui/combobox';
import { ExportDialog } from '../components/ExportDialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Badge } from '../components/ui/badge';
import { API } from '@/lib/apiBase';
import { getCategoryOptionsForSelect } from '@/lib/categoryUtils';
import { getProductOptionsForSelect } from '@/lib/productUtils';

// All available columns for the entries table
const ALL_COLUMNS = [
  { key: 'invoice_number', label: 'N° Facture', required: true, sortable: true },
  { key: 'supplier_name', label: 'Fournisseur', required: false, sortable: true },
  { key: 'payer_company_name', label: 'Destinataire', required: false, sortable: true },
  { key: 'invoice_date', label: 'Date', required: false, sortable: true },
  { key: 'lines', label: 'Lignes', required: false, sortable: true },
  { key: 'total_ht', label: 'Total HT', required: false, sortable: true, align: 'right' },
  { key: 'total_ttc', label: 'Total TTC', required: false, sortable: true, align: 'right' },
];

const DEFAULT_VISIBLE_COLUMNS = ['invoice_number', 'supplier_name', 'payer_company_name', 'invoice_date', 'lines', 'total_ht', 'total_ttc'];
const DEFAULT_COLUMN_ORDER = ['invoice_number', 'supplier_name', 'payer_company_name', 'invoice_date', 'lines', 'total_ht', 'total_ttc'];

// Sortable table header components (defined outside to avoid re-renders)
const SortIndicator = ({ column, sortColumn, sortDirection }) => {
  if (sortColumn !== column) return <ChevronUp className="w-3 h-3 opacity-30" />;
  return sortDirection === 'asc' 
    ? <ChevronUp className="w-3 h-3" /> 
    : <ChevronDown className="w-3 h-3" />;
};

const SortableHeader = ({ column, sortColumn, sortDirection, onSort, children, className = '' }) => (
  <TableHead 
    className={`cursor-pointer hover:bg-muted/50 select-none ${className}`}
    onClick={() => onSort(column)}
  >
    <div className="flex items-center gap-1">
      {children}
      <SortIndicator column={column} sortColumn={sortColumn} sortDirection={sortDirection} />
    </div>
  </TableHead>
);

const VAT_RATES = [
  { value: '0', label: '0%' },
  { value: '5.5', label: '5,5%' },
  { value: '10', label: '10%' },
  { value: '20', label: '20%' },
];

const BILLING_CYCLES = [
  { value: 'monthly', label: 'Mensuel' },
  { value: 'quarterly', label: 'Trimestriel' },
  { value: 'yearly', label: 'Annuel' },
];

const CURRENCIES = [
  { value: 'EUR', label: '€ EUR', symbol: '€' },
  { value: 'USD', label: '$ USD', symbol: '$' },
];

// Format currency with specific currency code
const formatCurrency = (amount, currency = 'EUR') => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency
  }).format(amount || 0);
};

const getCollapsedLineSummary = (line, currency) => {
  const company = (line.company_name || '').slice(0, 10) || '—';
  const userLabel = (line.user_name || '').trim();
  const user = userLabel
    ? userLabel.slice(0, 20)
    : ((line.sector_name || '').slice(0, 20) || '—');
  const ht = formatCurrency(line.amount_ht, currency);
  const ttc = formatCurrency(line.amount_ttc, currency);
  return `${company} · ${user} · ${ht} · ${ttc}`;
};

// Format amount with original currency and EUR equivalent if different
const formatAmountWithConversion = (originalAmount, eurAmount, currency = 'EUR') => {
  if (currency === 'EUR') {
    return formatCurrency(originalAmount, 'EUR');
  }
  return (
    <span className="inline-flex flex-col items-end gap-0.5">
      <span>{formatCurrency(originalAmount, currency)}</span>
      <span className="text-xs text-muted-foreground">({formatCurrency(eurAmount, 'EUR')})</span>
    </span>
  );
};

// Calculate line amounts
const calculateLineAmounts = (amountHt, vatRate) => {
  const ht = parseFloat(amountHt) || 0;
  const rate = parseFloat(vatRate) || 0;
  const vat = Math.round(ht * (rate / 100) * 100) / 100;
  const ttc = Math.round((ht + vat) * 100) / 100;
  return { amount_vat: vat, amount_ttc: ttc };
};

export const Entries = () => {
  const { token, canEdit } = useAuth();
  const [searchParams] = useSearchParams();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Advanced filters
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterPayer, setFilterPayer] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterAmountMin, setFilterAmountMin] = useState('');
  const [filterAmountMax, setFilterAmountMax] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Sorting
  const [sortColumn, setSortColumn] = useState('invoice_date');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  
  // Column preferences state
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const [columnOrder, setColumnOrder] = useState(DEFAULT_COLUMN_ORDER);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  
  // Dialog states
  const [wizardOpen, setWizardOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  
  // Split line dialog state
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [splitLineIndex, setSplitLineIndex] = useState(null);
  const [splitMode, setSplitMode] = useState('equal'); // 'equal' or 'prorata'
  const [splitCompanies, setSplitCompanies] = useState([]); // [{company_id, company_name, establishment_id, establishment_name, percentage, sector_id, sector_name, user_id, user_name}]
  
  // Wizard state
  const [entryAccordionOpen, setEntryAccordionOpen] = useState('description');
  const [collapsedLineIds, setCollapsedLineIds] = useState({});
  const [draggingLineIndex, setDraggingLineIndex] = useState(null);
  const [lineDropIndex, setLineDropIndex] = useState(null);
  const [lineDragGhost, setLineDragGhost] = useState(null);
  const lineRowRefs = useRef({});
  const lineDragStateRef = useRef({ from: null, to: null, offsetY: 0, left: 0, width: 0 });
  const [isEditing, setIsEditing] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  
  // Reference data
  const [suppliers, setSuppliers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [establishments, setEstablishments] = useState([]); // Flattened list of all establishments
  const [sectors, setSectors] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Form data
  const [entryData, setEntryData] = useState({
    invoice_number: '',
    supplier_id: '',
    supplier_name: '',
    payer_company_id: '',
    payer_company_name: '',
    invoice_date: new Date().toISOString().split('T')[0],
    file_id: null,
    file_name: null,
    currency: 'EUR',
    exchange_rate: 1.0,
    lines: []
  });
  
  // File upload state
  const [uploadingFile, setUploadingFile] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  
  // Currency state
  const [exchangeRateLoading, setExchangeRateLoading] = useState(false);
  const [exchangeRateInfo, setExchangeRateInfo] = useState(null);
  
  // Fetch all data
  const fetchEntries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/entries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Erreur lors du chargement des entrées');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchReferenceData = useCallback(async () => {
    try {
      const [suppliersRes, companiesRes, sectorsRes, usersRes, productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/external/suppliers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/external/companies`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/external/secteurs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/external/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/products`, {
          params: { include_inactive: true },
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/categories`, {
          params: { include_inactive: true },
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setSuppliers(suppliersRes.data);
      setCompanies(companiesRes.data);
      setSectors(sectorsRes.data);
      setUsers(usersRes.data);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      
      // Extract establishments from companies (flattened list)
      const allEstablishments = [];
      companiesRes.data.forEach(company => {
        const companyEstablishments = company.etablissements || [];
        companyEstablishments.forEach(etab => {
          allEstablishments.push({
            id: etab.id,
            name: etab.name,
            company_id: company.id,
            company_name: company.name
          });
        });
      });
      setEstablishments(allEstablishments);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  }, [token]);

  // Load user column preferences
  const loadColumnPreferences = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/preferences/entries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const prefs = response.data;
      
      if (prefs.visible_columns && prefs.visible_columns.length > 0) {
        // Use exactly what was saved, only filter out invalid columns
        const allColumnKeys = ALL_COLUMNS.map(c => c.key);
        const savedVisible = prefs.visible_columns.filter(k => allColumnKeys.includes(k));
        setVisibleColumns(savedVisible);
      }
      if (prefs.column_order && prefs.column_order.length > 0) {
        const allColumnKeys = ALL_COLUMNS.map(c => c.key);
        const savedOrder = prefs.column_order.filter(k => allColumnKeys.includes(k));
        // Add any truly new columns (added to code but not in saved prefs) at the end
        const newColumns = allColumnKeys.filter(k => !prefs.column_order.includes(k));
        setColumnOrder([...savedOrder, ...newColumns]);
      }
    } catch (error) {
      console.error('Error loading column preferences:', error);
    } finally {
      setPrefsLoaded(true);
    }
  }, [token]);

  // Save user column preferences
  const saveColumnPreferences = useCallback(async (visible, order) => {
    try {
      await axios.put(`${API}/user/preferences/entries`, {
        page: 'entries',
        visible_columns: visible,
        column_order: order
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Préférences sauvegardées');
    } catch (error) {
      console.error('Error saving column preferences:', error);
      toast.error('Erreur lors de la sauvegarde des préférences');
    }
  }, [token]);

  useEffect(() => {
    fetchEntries();
    fetchReferenceData();
    loadColumnPreferences();
  }, [fetchEntries, fetchReferenceData, loadColumnPreferences]);

  useEffect(() => {
    const dateFrom = searchParams.get('date_from');
    const dateTo = searchParams.get('date_to');
    if (!dateFrom && !dateTo) return;

    if (dateFrom) setFilterDateFrom(dateFrom);
    if (dateTo) setFilterDateTo(dateTo);
    setShowFilters(true);
  }, [searchParams]);

  // Toggle column visibility
  const toggleColumn = (columnKey) => {
    const column = ALL_COLUMNS.find(c => c.key === columnKey);
    if (column?.required) return;
    
    setVisibleColumns(prev => {
      if (prev.includes(columnKey)) {
        return prev.filter(k => k !== columnKey);
      } else {
        return [...prev, columnKey];
      }
    });
  };

  // Drag and drop handlers for column reordering
  const handleDragStart = (e, columnKey) => {
    setDraggedColumn(columnKey);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, columnKey) => {
    e.preventDefault();
    if (draggedColumn === columnKey) return;
    
    setColumnOrder(prev => {
      const newOrder = [...prev];
      const draggedIndex = newOrder.indexOf(draggedColumn);
      const targetIndex = newOrder.indexOf(columnKey);
      
      if (draggedIndex !== -1 && targetIndex !== -1) {
        newOrder.splice(draggedIndex, 1);
        newOrder.splice(targetIndex, 0, draggedColumn);
      }
      return newOrder;
    });
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  // Save settings and close dialog
  const handleSaveSettings = () => {
    saveColumnPreferences(visibleColumns, columnOrder);
    setSettingsDialogOpen(false);
  };

  // Reset settings to defaults
  const handleResetSettings = () => {
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    setColumnOrder(DEFAULT_COLUMN_ORDER);
  };

  // Get ordered and visible columns for display
  const displayColumns = useMemo(() => {
    return columnOrder
      .filter(key => visibleColumns.includes(key))
      .map(key => ALL_COLUMNS.find(c => c.key === key))
      .filter(Boolean);
  }, [columnOrder, visibleColumns]);

  // Get cell value for a column
  const getCellValue = (entry, columnKey) => {
    switch (columnKey) {
      case 'invoice_number':
        return <span className="font-medium">{entry.invoice_number}</span>;
      case 'supplier_name':
        return <span className="text-sm">{entry.supplier_name}</span>;
      case 'payer_company_name':
        return <span className="text-sm">{entry.payer_company_name}</span>;
      case 'invoice_date':
        return (
          <span className="text-sm">
            {new Date(entry.invoice_date).toLocaleDateString('fr-FR')}
            {entry.currency && entry.currency !== 'EUR' && (
              <span className="ml-1 text-xs text-blue-500 font-medium">({entry.currency})</span>
            )}
          </span>
        );
      case 'lines':
        return <span className="text-sm">{entry.lines?.length || 0}</span>;
      case 'total_ht':
        return (
          <span className="text-sm">
            {formatAmountWithConversion(entry.total_ht, entry.total_ht_eur, entry.currency)}
          </span>
        );
      case 'total_ttc':
        return (
          <span className="font-medium">
            {formatAmountWithConversion(entry.total_ttc, entry.total_ttc_eur, entry.currency)}
          </span>
        );
      default:
        return entry[columnKey] || '-';
    }
  };

  // Reset wizard
  const resetWizard = () => {
    setEntryAccordionOpen('description');
    setCollapsedLineIds({});
    setDraggingLineIndex(null);
    setLineDropIndex(null);
    setLineDragGhost(null);
    setIsEditing(false);
    setIsViewing(false);
    setIsDuplicating(false);
    setSelectedEntry(null);
    setExchangeRateInfo(null);
    setEntryData({
      invoice_number: '',
      supplier_id: '',
      supplier_name: '',
      payer_company_id: '',
      payer_company_name: '',
      invoice_date: new Date().toISOString().split('T')[0],
      file_id: null,
      file_name: null,
      currency: 'EUR',
      exchange_rate: 1.0,
      lines: []
    });
  };

  // File upload handlers
  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.pdf'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExt)) {
      toast.error('Type de fichier non autorisé. Formats acceptés: JPG, JPEG, PNG, PDF');
      return;
    }

    // Validate file size (10 Mo)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Fichier trop volumineux. Taille max: 10 Mo');
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/files/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setEntryData(prev => ({
        ...prev,
        file_id: response.data.id,
        file_name: response.data.original_name
      }));
      
      toast.success('Fichier uploadé avec succès');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload du fichier');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleFileRemove = async () => {
    if (entryData.file_id) {
      try {
        // Delete the file from backend
        await axios.delete(`${API}/files/${entryData.file_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // If editing an existing entry, update it to remove file reference
        if (isEditing && selectedEntry?.id) {
          await axios.put(`${API}/entries/${selectedEntry.id}`, {
            ...entryData,
            file_id: null,
            file_name: null
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          // Refresh entries list to reflect the change
          fetchEntries();
          toast.success('Fichier supprimé');
        }
      } catch (error) {
        console.error('Error deleting file:', error);
        toast.error('Erreur lors de la suppression du fichier');
      }
    }
    // Update local state
    setEntryData(prev => ({
      ...prev,
      file_id: null,
      file_name: null
    }));
  };

  // Open file with authentication (download and display in new tab)
  const openFileWithAuth = async (fileId, fileName) => {
    try {
      const response = await axios.get(`${API}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Get content type from response or infer from filename
      let contentType = response.headers['content-type'] || 'application/octet-stream';
      
      // If content type is generic, try to infer from filename
      if (contentType === 'application/octet-stream' && fileName) {
        const ext = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
        const mimeTypes = {
          '.pdf': 'application/pdf',
          '.jpg': 'image/jpeg',
          '.jpeg': 'image/jpeg',
          '.png': 'image/png'
        };
        contentType = mimeTypes[ext] || contentType;
      }
      
      // Create blob with correct type
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      
      // For PDFs and images, open in new tab. For others, trigger download
      if (contentType.startsWith('image/') || contentType === 'application/pdf') {
        window.open(blobUrl, '_blank');
      } else {
        // Trigger download
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName || 'fichier';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      // Clean up blob URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      console.error('Error opening file:', error);
      toast.error('Erreur lors de l\'ouverture du fichier');
    }
  };

  // File drag & drop handlers
  const handleFileDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleFileDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return File;
    const ext = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    if (['.jpg', '.jpeg', '.png'].includes(ext)) return Image;
    if (ext === '.pdf') return FileText;
    return File;
  };

  // Open wizard for new entry
  const openNewEntry = () => {
    resetWizard();
    setWizardOpen(true);
  };

  const populateEntryForm = useCallback((entry) => {
    const linesWithCategory = (entry.lines || []).map((line) => {
      let categoryId = line.category_id;
      if (!categoryId && line.product_id) {
        const product = products.find((p) => p.id === line.product_id);
        categoryId = product?.category_id || '';
      }
      return {
        ...line,
        category_id: categoryId || '',
      };
    });

    setEntryData({
      invoice_number: entry.invoice_number || '',
      supplier_id: entry.supplier_id || '',
      supplier_name: entry.supplier_name || '',
      payer_company_id: entry.payer_company_id || '',
      payer_company_name: entry.payer_company_name || '',
      invoice_date: entry.invoice_date || new Date().toISOString().split('T')[0],
      file_id: entry.file_id || null,
      file_name: entry.file_name || null,
      currency: entry.currency || 'EUR',
      exchange_rate: entry.exchange_rate || 1.0,
      lines: linesWithCategory,
    });

    if (entry.currency === 'USD') {
      setExchangeRateInfo({
        rate: entry.exchange_rate,
        date: entry.invoice_date,
        source: 'Valeur enregistrée',
      });
    } else {
      setExchangeRateInfo(null);
    }
  }, [products]);

  // Open sidebar on récapitulatif (view mode)
  const openViewEntry = useCallback((entry) => {
    setIsViewing(true);
    setIsEditing(false);
    setIsDuplicating(false);
    setSelectedEntry(entry);
    populateEntryForm(entry);
    setEntryAccordionOpen('recapitulatif');
    setWizardOpen(true);
  }, [populateEntryForm]);

  // Open wizard for editing
  const openEditEntry = useCallback((entry) => {
    setIsViewing(false);
    setIsEditing(true);
    setIsDuplicating(false);
    setSelectedEntry(entry);
    populateEntryForm(entry);
    setEntryAccordionOpen(undefined);
    setWizardOpen(true);
  }, [populateEntryForm]);

  // Open wizard for duplicating
  const openDuplicateEntry = useCallback((entry) => {
    setIsDuplicating(true);
    setIsEditing(false);
    setIsViewing(false);
    setSelectedEntry(entry);
    
    // Map lines to include category_id - same as edit
    const linesWithCategory = (entry.lines || []).map(line => {
      let categoryId = line.category_id;
      if (!categoryId && line.product_id) {
        const product = products.find(p => p.id === line.product_id);
        categoryId = product?.category_id || '';
      }
      return {
        ...line,
        category_id: categoryId || '',
        reconduire_marked: false,
      };
    });
    
    // Pre-fill all data except: invoice_number, invoice_date, file_url, file_name
    setEntryData({
      invoice_number: '', // Vide pour duplication
      supplier_id: entry.supplier_id || '',
      supplier_name: entry.supplier_name || '',
      payer_company_id: entry.payer_company_id || '',
      payer_company_name: entry.payer_company_name || '',
      invoice_date: new Date().toISOString().split('T')[0], // Date du jour
      file_url: null, // Pas de pièce jointe
      file_name: null,
      currency: entry.currency || 'EUR',
      exchange_rate: entry.exchange_rate || 1.0,
      lines: linesWithCategory
    });
    
    // Set exchange rate info if USD
    if (entry.currency === 'USD') {
      setExchangeRateInfo({
        rate: entry.exchange_rate,
        date: entry.invoice_date,
        source: 'Valeur de l\'entrée originale'
      });
    }

    setEntryAccordionOpen('description');
    setCollapsedLineIds(
      Object.fromEntries(linesWithCategory.map((line) => [line.id, true])),
    );
    setWizardOpen(true);
  }, [products]);

  // Fetch exchange rate from API
  const fetchExchangeRate = async (date, currency = 'USD') => {
    if (currency === 'EUR') {
      setEntryData(prev => ({ ...prev, exchange_rate: 1.0 }));
      setExchangeRateInfo(null);
      return;
    }
    
    setExchangeRateLoading(true);
    try {
      const response = await axios.get(`${API}/exchange-rate`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { from_currency: currency, to_currency: 'EUR', date }
      });
      
      const rate = response.data.rate;
      setEntryData(prev => ({ ...prev, exchange_rate: rate }));
      setExchangeRateInfo({
        rate: rate,
        date: response.data.date,
        source: response.data.source,
        warning: response.data.warning
      });
    } catch (error) {
      console.error('Error fetching exchange rate:', error);
      // Use fallback rate
      const fallbackRate = 0.92;
      setEntryData(prev => ({ ...prev, exchange_rate: fallbackRate }));
      setExchangeRateInfo({
        rate: fallbackRate,
        date: date,
        source: 'Taux de secours',
        warning: 'Impossible de récupérer le taux en ligne'
      });
      toast.warning('Utilisation du taux de change par défaut');
    } finally {
      setExchangeRateLoading(false);
    }
  };

  // Handle currency change
  const handleCurrencyChange = async (newCurrency) => {
    setEntryData(prev => ({ ...prev, currency: newCurrency }));
    if (newCurrency !== 'EUR' && entryData.invoice_date) {
      await fetchExchangeRate(entryData.invoice_date, newCurrency);
    } else {
      setEntryData(prev => ({ ...prev, exchange_rate: 1.0 }));
      setExchangeRateInfo(null);
    }
  };

  // Handle invoice date change (may need to update exchange rate)
  const handleInvoiceDateChange = async (newDate) => {
    setEntryData(prev => ({ ...prev, invoice_date: newDate }));
    if (entryData.currency !== 'EUR' && newDate) {
      await fetchExchangeRate(newDate, entryData.currency);
    }
  };

  // Toggle line expand/collapse in lignes tab
  const toggleLineCollapsed = (lineId) => {
    setCollapsedLineIds((prev) => ({
      ...prev,
      [lineId]: !prev[lineId],
    }));
  };

  const reorderLines = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    setEntryData((prev) => {
      const lines = [...prev.lines];
      const [moved] = lines.splice(fromIndex, 1);
      lines.splice(toIndex, 0, moved);
      return { ...prev, lines };
    });
  };

  const isLineDragInteractiveTarget = (target) =>
    target.closest('button, label, input, [role="checkbox"], a');

  const handleLineDragMouseDown = (e, index, lineId) => {
    if (!isDuplicating || !collapsedLineIds[lineId]) return;
    if (isLineDragInteractiveTarget(e.target)) return;
    e.preventDefault();
    const el = lineRowRefs.current[lineId];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    lineDragStateRef.current = {
      from: index,
      to: index,
      offsetY,
      left: rect.left,
      width: rect.width,
    };
    setDraggingLineIndex(index);
    setLineDropIndex(index);
    setLineDragGhost({
      index,
      x: rect.left,
      y: e.clientY - offsetY,
      width: rect.width,
    });
  };

  useEffect(() => {
    if (draggingLineIndex === null) return undefined;

    const handleMouseMove = (e) => {
      const { offsetY, left, width } = lineDragStateRef.current;
      setLineDragGhost((prev) => (
        prev
          ? { ...prev, x: left, y: e.clientY - offsetY, width }
          : null
      ));

      const ids = entryData.lines.map((line) => line.id);
      let nextDrop = lineDragStateRef.current.from ?? 0;
      let matched = false;

      for (let i = 0; i < ids.length; i += 1) {
        const el = lineRowRefs.current[ids[i]];
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (e.clientY < rect.top) {
          nextDrop = i;
          matched = true;
          break;
        }
        if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
          nextDrop = e.clientY < rect.top + rect.height / 2
            ? i
            : Math.min(i + 1, ids.length - 1);
          matched = true;
          break;
        }
      }

      if (!matched && ids.length > 0) {
        const lastEl = lineRowRefs.current[ids[ids.length - 1]];
        if (lastEl && e.clientY > lastEl.getBoundingClientRect().bottom) {
          nextDrop = ids.length - 1;
        }
      }

      lineDragStateRef.current.to = nextDrop;
      setLineDropIndex(nextDrop);
    };

    const handleMouseUp = () => {
      const { from, to } = lineDragStateRef.current;
      if (from !== null && to !== null && from !== to) {
        reorderLines(from, to);
      }
      lineDragStateRef.current = { from: null, to: null, offsetY: 0, left: 0, width: 0 };
      setDraggingLineIndex(null);
      setLineDropIndex(null);
      setLineDragGhost(null);
    };

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingLineIndex, entryData.lines]);

  // Add new line
  const addLine = () => {
    const newLine = {
      id: `temp-${Date.now()}`,
      category_id: '',
      product_id: '',
      product_name: '',
      entry_type: 'subscription',
      billing_cycle: 'monthly',
      company_id: '',
      company_name: '',
      establishment_id: '',
      establishment_name: '',
      sector_id: '',
      sector_name: '',
      user_id: '',
      user_name: '',
      amount_ht: 0,
      vat_rate: 20,
      amount_vat: 0,
      amount_ttc: 0,
      reconduire_marked: false,
    };
    setEntryData(prev => ({
      ...prev,
      lines: [...prev.lines, newLine]
    }));
    if (isDuplicating) {
      setCollapsedLineIds((prev) => ({ ...prev, [newLine.id]: true }));
    }
  };

  // Update line
  const updateLine = (index, field, value) => {
    setEntryData(prev => {
      const newLines = [...prev.lines];
      newLines[index] = { ...newLines[index], [field]: value };
      
      // Auto-calculate amounts when HT or VAT rate changes
      if (field === 'amount_ht' || field === 'vat_rate') {
        const amounts = calculateLineAmounts(
          field === 'amount_ht' ? value : newLines[index].amount_ht,
          field === 'vat_rate' ? value : newLines[index].vat_rate
        );
        newLines[index].amount_vat = amounts.amount_vat;
        newLines[index].amount_ttc = amounts.amount_ttc;
      }
      
      // Reset product when category changes (cascading filter)
      if (field === 'category_id') {
        newLines[index].product_id = '';
        newLines[index].product_name = '';
      }
      
      // Auto-fill VAT rate when product is selected
      if (field === 'product_id') {
        const product = products.find(p => p.id === value);
        if (product) {
          newLines[index].product_name = product.name;
          newLines[index].vat_rate = product.default_vat_rate;
          // Also set category_id if not already set
          if (!newLines[index].category_id && product.category_id) {
            newLines[index].category_id = product.category_id;
          }
          const amounts = calculateLineAmounts(newLines[index].amount_ht, product.default_vat_rate);
          newLines[index].amount_vat = amounts.amount_vat;
          newLines[index].amount_ttc = amounts.amount_ttc;
        }
      }
      
      // Set company name when company is selected
      if (field === 'company_id') {
        const company = companies.find(c => c.id === value);
        if (company) {
          newLines[index].company_name = company.name;
        }
        // Reset establishment, sector and user when company changes (cascading filter)
        newLines[index].establishment_id = '';
        newLines[index].establishment_name = '';
        newLines[index].sector_id = '';
        newLines[index].sector_name = '';
        newLines[index].user_id = '';
        newLines[index].user_name = '';
        // No auto-selection of establishment - user can choose or leave empty
      }
      
      // Set establishment name when establishment is selected
      if (field === 'establishment_id') {
        const establishment = establishments.find(e => e.id === value);
        if (establishment) {
          newLines[index].establishment_name = establishment.name;
        }
        // Reset sector and user when establishment changes (cascading filter)
        newLines[index].sector_id = '';
        newLines[index].sector_name = '';
        newLines[index].user_id = '';
        newLines[index].user_name = '';
      }
      
      // Set sector name when sector is selected
      if (field === 'sector_id') {
        const sector = sectors.find(s => s.id === value);
        if (sector) {
          newLines[index].sector_name = sector.name;
        }
        // Reset user when sector changes (cascading filter)
        const currentUser = newLines[index].user_id;
        if (currentUser && value) {
          // Users have service_id which corresponds to sector.id
          const validUsers = users.filter(u => u.service_id === value);
          const userStillValid = validUsers.some(u => u.id === currentUser);
          if (!userStillValid) {
            newLines[index].user_id = '';
            newLines[index].user_name = '';
          }
        }
      }
      
      // Set user name when user is selected
      if (field === 'user_id') {
        const user = users.find(u => u.id === value);
        if (user) {
          newLines[index].user_name = user.display_name || `${user.first_name} ${user.last_name}`;
        }
      }
      
      return { ...prev, lines: newLines };
    });
  };

  // Remove line
  const removeLine = (index) => {
    setEntryData(prev => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== index)
    }));
  };

  // Open split dialog for a line
  const openSplitDialog = (index) => {
    setSplitLineIndex(index);
    setSplitMode('equal');
    setSplitCompanies([]);
    setSplitDialogOpen(true);
  };

  // Toggle company selection in split dialog
  const toggleSplitCompany = (company) => {
    setSplitCompanies(prev => {
      const exists = prev.find(c => c.company_id === company.id);
      if (exists) {
        return prev.filter(c => c.company_id !== company.id);
      } else {
        // No auto-selection of establishment - user can choose or leave empty
        return [...prev, {
          company_id: company.id,
          company_name: company.name,
          establishment_id: '',
          establishment_name: '',
          percentage: 0,
          sector_id: '',
          sector_name: '',
          user_id: '',
          user_name: ''
        }];
      }
    });
  };

  // Update split company data (percentage, establishment, sector, user)
  const updateSplitCompany = (companyId, field, value) => {
    setSplitCompanies(prev => prev.map(c => {
      if (c.company_id !== companyId) return c;
      
      const updated = { ...c, [field]: value };
      
      // Handle cascading selection
      if (field === 'establishment_id') {
        const establishment = establishments.find(e => e.id === value);
        updated.establishment_name = establishment?.name || '';
        // Reset sector and user when establishment changes
        updated.sector_id = '';
        updated.sector_name = '';
        updated.user_id = '';
        updated.user_name = '';
      }
      if (field === 'sector_id') {
        const sector = sectors.find(s => s.id === value);
        updated.sector_name = sector?.name || '';
        // Reset user if sector changes
        const validUsers = users.filter(u => u.service_id === value);
        if (!validUsers.some(u => u.id === c.user_id)) {
          updated.user_id = '';
          updated.user_name = '';
        }
      }
      if (field === 'user_id') {
        const user = users.find(u => u.id === value);
        updated.user_name = user?.display_name || (user ? `${user.first_name} ${user.last_name}` : '');
      }
      
      return updated;
    }));
  };

  // Calculate split preview
  const getSplitPreview = () => {
    if (splitLineIndex === null || splitCompanies.length === 0) return [];
    
    const sourceLine = entryData.lines[splitLineIndex];
    // Guard against undefined source line (can happen after split execution)
    if (!sourceLine) return [];
    
    const totalHt = parseFloat(sourceLine.amount_ht) || 0;
    const vatRate = sourceLine.vat_rate;
    
    if (splitMode === 'equal') {
      const count = splitCompanies.length;
      const baseAmount = Math.floor((totalHt / count) * 100) / 100;
      const remainder = Math.round((totalHt - baseAmount * count) * 100) / 100;
      
      return splitCompanies.map((company, index) => {
        // First company gets the rounding remainder
        const amount = index === 0 ? baseAmount + remainder : baseAmount;
        const amounts = calculateLineAmounts(amount, vatRate);
        return {
          ...company,
          amount_ht: amount,
          amount_vat: amounts.amount_vat,
          amount_ttc: amounts.amount_ttc,
          displayTag: `${index + 1}/${count}`
        };
      });
    } else {
      // Prorata mode
      return splitCompanies.map((company) => {
        const percentage = parseFloat(company.percentage) || 0;
        const amount = Math.round((totalHt * percentage / 100) * 100) / 100;
        const amounts = calculateLineAmounts(amount, vatRate);
        return {
          ...company,
          amount_ht: amount,
          amount_vat: amounts.amount_vat,
          amount_ttc: amounts.amount_ttc,
          displayTag: `${percentage}%`
        };
      });
    }
  };

  // Calculate total percentage for prorata validation
  const getTotalPercentage = () => {
    return splitCompanies.reduce((sum, c) => sum + (parseFloat(c.percentage) || 0), 0);
  };

  // Execute the split - replace source line with new lines
  const executeSplit = () => {
    const preview = getSplitPreview();
    const sourceLine = entryData.lines[splitLineIndex];
    
    if (!sourceLine || preview.length === 0) {
      toast.error('Erreur lors de la division');
      setSplitDialogOpen(false);
      return;
    }
    
    // Create new lines from preview
    const newLines = preview.map((p, idx) => ({
      id: `temp-${Date.now()}-${idx}`,
      category_id: sourceLine.category_id,
      product_id: sourceLine.product_id,
      product_name: sourceLine.product_name,
      entry_type: sourceLine.entry_type,
      billing_cycle: sourceLine.billing_cycle,
      company_id: p.company_id,
      company_name: p.company_name,
      establishment_id: p.establishment_id,
      establishment_name: p.establishment_name,
      sector_id: p.sector_id,
      sector_name: p.sector_name,
      user_id: p.user_id,
      user_name: p.user_name,
      amount_ht: p.amount_ht,
      vat_rate: sourceLine.vat_rate,
      amount_vat: p.amount_vat,
      amount_ttc: p.amount_ttc,
      split_tag: p.displayTag // Visual indicator
    }));
    
    // Replace source line with new lines
    setEntryData(prev => {
      const lines = [...prev.lines];
      lines.splice(splitLineIndex, 1, ...newLines);
      return { ...prev, lines };
    });
    
    // Reset split state BEFORE closing dialog
    setSplitLineIndex(null);
    setSplitCompanies([]);
    setSplitDialogOpen(false);
    toast.success(`Ligne divisée en ${newLines.length} lignes`);
  };

  // Check if split can be executed
  const canExecuteSplit = () => {
    if (splitCompanies.length < 2) return false;
    if (splitMode === 'prorata') {
      const total = getTotalPercentage();
      return Math.abs(total - 100) < 0.01; // Allow small floating point errors
    }
    return true;
  };

  // Calculate totals (including EUR conversion)
  const calculateTotals = () => {
    const totalHt = entryData.lines.reduce((sum, line) => sum + (parseFloat(line.amount_ht) || 0), 0);
    const totalVat = entryData.lines.reduce((sum, line) => sum + (parseFloat(line.amount_vat) || 0), 0);
    const totalTtc = entryData.lines.reduce((sum, line) => sum + (parseFloat(line.amount_ttc) || 0), 0);
    
    const rate = entryData.exchange_rate || 1.0;
    const totalHtEur = entryData.currency === 'EUR' ? totalHt : Math.round(totalHt * rate * 100) / 100;
    const totalVatEur = entryData.currency === 'EUR' ? totalVat : Math.round(totalVat * rate * 100) / 100;
    const totalTtcEur = entryData.currency === 'EUR' ? totalTtc : Math.round(totalTtc * rate * 100) / 100;
    
    return {
      total_ht: Math.round(totalHt * 100) / 100,
      total_vat: Math.round(totalVat * 100) / 100,
      total_ttc: Math.round(totalTtc * 100) / 100,
      total_ht_eur: totalHtEur,
      total_vat_eur: totalVatEur,
      total_ttc_eur: totalTtcEur
    };
  };

  // Validate step 1
  const validateStep1 = () => {
    if (!entryData.invoice_number.trim()) {
      toast.error('Le numéro de facture est requis');
      return false;
    }
    if (!entryData.supplier_name.trim()) {
      toast.error('Le fournisseur est requis');
      return false;
    }
    if (!entryData.payer_company_name.trim()) {
      toast.error('Le destinataire (société payeuse) est requis');
      return false;
    }
    if (!entryData.invoice_date) {
      toast.error('La date de facture est requise');
      return false;
    }
    return true;
  };

  // Validate step 2
  const validateStep2 = () => {
    if (entryData.lines.length === 0) {
      toast.error('Au moins une ligne est requise');
      return false;
    }
    for (let i = 0; i < entryData.lines.length; i++) {
      const line = entryData.lines[i];
      if (!line.product_name.trim()) {
        toast.error(`Ligne ${i + 1}: Le produit est requis`);
        return false;
      }
      if (line.amount_ht === '' || line.amount_ht === null || line.amount_ht === undefined) {
        toast.error(`Ligne ${i + 1}: Le montant HT est requis`);
        return false;
      }
      if (parseFloat(line.amount_ht) === 0) {
        toast.error(`Ligne ${i + 1}: Le montant HT ne peut pas être égal à 0`);
        return false;
      }
    }
    return true;
  };

  // Submit entry
  const submitEntry = async () => {
    if (!validateStep1() || !validateStep2()) return;

    try {
      const payload = {
        ...entryData,
        currency: entryData.currency,
        exchange_rate: entryData.exchange_rate,
        lines: entryData.lines.map(line => {
          // Get category name from categories if category_id is set
          const category = categories.find(c => c.id === line.category_id);
          return {
            product_id: line.product_id || null,
            product_name: line.product_name,
            category_id: line.category_id || null,
            category_name: category?.name || null,
            entry_type: line.entry_type,
            billing_cycle: line.entry_type === 'subscription' ? line.billing_cycle : null,
            company_id: line.company_id || null,
            company_name: line.company_name || null,
            establishment_id: line.establishment_id || null,
            establishment_name: line.establishment_name || null,
            sector_id: line.sector_id || null,
            sector_name: line.sector_name || null,
            user_id: line.user_id || null,
            user_name: line.user_name || null,
            amount_ht: parseFloat(line.amount_ht) || 0,
            vat_rate: line.vat_rate !== undefined && line.vat_rate !== null && line.vat_rate !== '' ? parseFloat(line.vat_rate) : 20,
            currency: entryData.currency,
            exchange_rate: entryData.exchange_rate,
          };
        })
      };

      if (isEditing && selectedEntry) {
        await axios.put(`${API}/entries/${selectedEntry.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Entrée mise à jour');
      } else {
        await axios.post(`${API}/entries`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(isDuplicating ? 'Entrée reconduite avec succès' : 'Entrée créée');
      }

      setWizardOpen(false);
      resetWizard();
      fetchEntries();
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // Delete entry
  const handleDelete = async () => {
    if (!selectedEntry) return;
    try {
      await axios.delete(`${API}/entries/${selectedEntry.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entrée supprimée');
      setDeleteDialogOpen(false);
      setSelectedEntry(null);
      fetchEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Get unique suppliers and payers from entries for filter options
  const supplierOptions = useMemo(() => {
    const uniqueSuppliers = [...new Set(entries.map(e => e.supplier_name).filter(Boolean))];
    return uniqueSuppliers.sort().map(s => ({ value: s, label: s }));
  }, [entries]);

  const payerOptions = useMemo(() => {
    const uniquePayers = [...new Set(entries.map(e => e.payer_company_name).filter(Boolean))];
    return uniquePayers.sort().map(p => ({ value: p, label: p }));
  }, [entries]);

  // Advanced filtering, sorting and pagination
  const filteredAndSortedEntries = useMemo(() => {
    let result = [...entries];
    
    // Text search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(entry =>
        entry.invoice_number?.toLowerCase().includes(search) ||
        entry.supplier_name?.toLowerCase().includes(search) ||
        entry.payer_company_name?.toLowerCase().includes(search)
      );
    }
    
    // Supplier filter
    if (filterSupplier) {
      result = result.filter(entry => entry.supplier_name === filterSupplier);
    }
    
    // Payer filter
    if (filterPayer) {
      result = result.filter(entry => entry.payer_company_name === filterPayer);
    }
    
    // Date range filter
    if (filterDateFrom) {
      result = result.filter(entry => entry.invoice_date >= filterDateFrom);
    }
    if (filterDateTo) {
      result = result.filter(entry => entry.invoice_date <= filterDateTo);
    }
    
    // Amount range filter
    if (filterAmountMin) {
      const min = parseFloat(filterAmountMin);
      result = result.filter(entry => (entry.total_ht || 0) >= min);
    }
    if (filterAmountMax) {
      const max = parseFloat(filterAmountMax);
      result = result.filter(entry => (entry.total_ht || 0) <= max);
    }
    
    // Sorting
    result.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortColumn) {
        case 'invoice_number':
          aVal = a.invoice_number || '';
          bVal = b.invoice_number || '';
          break;
        case 'supplier_name':
          aVal = a.supplier_name || '';
          bVal = b.supplier_name || '';
          break;
        case 'payer_company_name':
          aVal = a.payer_company_name || '';
          bVal = b.payer_company_name || '';
          break;
        case 'invoice_date':
          aVal = a.invoice_date || '';
          bVal = b.invoice_date || '';
          break;
        case 'lines':
          aVal = a.lines?.length || 0;
          bVal = b.lines?.length || 0;
          break;
        case 'total_ht':
          aVal = a.total_ht || 0;
          bVal = b.total_ht || 0;
          break;
        case 'total_ttc':
          aVal = a.total_ttc || 0;
          bVal = b.total_ttc || 0;
          break;
        default:
          aVal = a.invoice_date || '';
          bVal = b.invoice_date || '';
      }
      
      if (typeof aVal === 'string') {
        const compare = aVal.localeCompare(bVal, 'fr');
        return sortDirection === 'asc' ? compare : -compare;
      } else {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
    });
    
    return result;
  }, [entries, searchTerm, filterSupplier, filterPayer, filterDateFrom, filterDateTo, filterAmountMin, filterAmountMax, sortColumn, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedEntries.length / itemsPerPage);
  const paginatedEntries = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedEntries.slice(start, start + itemsPerPage);
  }, [filteredAndSortedEntries, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterSupplier, filterPayer, filterDateFrom, filterDateTo, filterAmountMin, filterAmountMax, itemsPerPage]);

  // Handle column sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setFilterSupplier('');
    setFilterPayer('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterAmountMin('');
    setFilterAmountMax('');
    setCurrentPage(1);
  };

  // Check if any filter is active
  const hasActiveFilters = searchTerm || filterSupplier || filterPayer || filterDateFrom || filterDateTo || filterAmountMin || filterAmountMax;

  // Selection functions
  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedEntries.length && paginatedEntries.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedEntries.map(e => e.id)));
    }
  };

  const toggleSelectOne = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const isAllSelected = paginatedEntries.length > 0 && selectedIds.size === paginatedEntries.length;
  const isSomeSelected = selectedIds.size > 0 && selectedIds.size < paginatedEntries.length;

  // Clear selection when filters/pagination change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchTerm, filterSupplier, filterPayer, filterDateFrom, filterDateTo, filterAmountMin, filterAmountMax, currentPage, itemsPerPage]);

  // Bulk delete
  const handleBulkDelete = async () => {
    try {
      const deletePromises = Array.from(selectedIds).map(id =>
        axios.delete(`${API}/entries/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );
      await Promise.all(deletePromises);
      toast.success(`${selectedIds.size} entrée(s) supprimée(s)`);
      setSelectedIds(new Set());
      setBulkDeleteDialogOpen(false);
      fetchEntries();
    } catch (error) {
      console.error('Error deleting entries:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Get entries for export (selected or filtered)
  const getEntriesForExport = useMemo(() => {
    if (selectedIds.size > 0) {
      return filteredAndSortedEntries.filter(e => selectedIds.has(e.id));
    }
    return filteredAndSortedEntries;
  }, [selectedIds, filteredAndSortedEntries]);

  // Get sectors filtered by company
  const getSectorsForCompany = (companyId) => {
    if (!companyId) return sectors;
    return sectors.filter(s => s.company_id === companyId);
  };

  // Get establishments filtered by company
  const getEstablishmentsForCompany = (companyId) => {
    if (!companyId) return establishments;
    return establishments.filter(e => e.company_id === companyId);
  };

  // Get sectors filtered by establishment (if establishment_id exists) or by company
  const getSectorsForEstablishment = (establishmentId, companyId) => {
    // First try to filter by establishment if sectors have establishment_id
    if (establishmentId) {
      const sectorsWithEstab = sectors.filter(s => s.establishment_id === establishmentId);
      if (sectorsWithEstab.length > 0) return sectorsWithEstab;
    }
    // Fallback to filtering by company
    if (companyId) {
      return sectors.filter(s => s.company_id === companyId);
    }
    return sectors;
  };

  // Get users filtered by sector (priority) or company
  const getUsersForSectorOrCompany = (sectorId, companyId) => {
    if (sectorId) {
      // Filter users by sector (users have service_id which corresponds to sector.id)
      return users.filter(u => u.service_id === sectorId);
    }
    if (companyId) {
      // Filter users by company
      return users.filter(u => u.company_id === companyId);
    }
    return users;
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="entries-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Entrées</h1>
        <p className="text-muted-foreground">
          Gérez vos factures et leurs lignes de coûts
          <span className="ml-2 text-sm font-medium text-primary">
            ({filteredAndSortedEntries.length} entrée{filteredAndSortedEntries.length > 1 ? 's' : ''} trouvée{filteredAndSortedEntries.length > 1 ? 's' : ''})
            {selectedIds.size > 0 && (
              <span className="text-orange-500"> • {selectedIds.size} sélectionnée{selectedIds.size > 1 ? 's' : ''}</span>
            )}
          </span>
        </p>
      </div>

      {/* Toolbar: Search + Filters + Buttons */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative w-80">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher (N° facture, fournisseur, destinataire)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-entries"
            />
          </div>
          <Button 
            variant={showFilters ? "default" : "outline"} 
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtres avancés
            {hasActiveFilters && <span className="ml-2 w-2 h-2 bg-red-500 rounded-full" />}
          </Button>
          {hasActiveFilters && (
            <Button variant="ghost" onClick={resetFilters} data-testid="reset-filters-btn">
              <RotateCcw className="w-4 h-4 mr-2" />
              Réinitialiser
            </Button>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {canEdit && selectedIds.size > 0 && (
            <Button 
              variant="destructive" 
              onClick={() => setBulkDeleteDialogOpen(true)} 
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer ({selectedIds.size})
            </Button>
          )}
          <Button variant="outline" onClick={() => setExportDialogOpen(true)} data-testid="export-entries-btn">
            <Download className="w-4 h-4 mr-2" />
            Exporter {selectedIds.size > 0 ? `(${selectedIds.size})` : hasActiveFilters ? `(${filteredAndSortedEntries.length})` : ''}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setSettingsDialogOpen(true)}
            data-testid="settings-entries-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Paramètres
          </Button>
          {canEdit && (
            <Button onClick={openNewEntry} data-testid="add-entry-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle entrée
            </Button>
          )}
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <div className="bg-card rounded-lg border border-border p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {/* Supplier Filter */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Fournisseur</Label>
              <Combobox
                options={supplierOptions}
                value={filterSupplier}
                onValueChange={setFilterSupplier}
                placeholder="Tous"
                searchPlaceholder="Rechercher..."
                emptyText="Aucun fournisseur"
                data-testid="filter-supplier"
              />
            </div>
            
            {/* Payer Filter */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Destinataire</Label>
              <Combobox
                options={payerOptions}
                value={filterPayer}
                onValueChange={setFilterPayer}
                placeholder="Tous"
                searchPlaceholder="Rechercher..."
                emptyText="Aucun destinataire"
                data-testid="filter-payer"
              />
            </div>
            
            {/* Date From */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Date du</Label>
              <Input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="h-9"
                data-testid="filter-date-from"
              />
            </div>
            
            {/* Date To */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Date au</Label>
              <Input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="h-9"
                data-testid="filter-date-to"
              />
            </div>
            
            {/* Amount Min */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Montant min (€)</Label>
              <Input
                type="number"
                placeholder="0"
                value={filterAmountMin}
                onChange={(e) => setFilterAmountMin(e.target.value)}
                className="h-9"
                data-testid="filter-amount-min"
              />
            </div>
            
            {/* Amount Max */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Montant max (€)</Label>
              <Input
                type="number"
                placeholder="∞"
                value={filterAmountMax}
                onChange={(e) => setFilterAmountMax(e.target.value)}
                className="h-9"
                data-testid="filter-amount-max"
              />
            </div>
          </div>
        </div>
      )}

      {/* Entries Table */}
      <div className="bg-card rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox 
                  checked={isAllSelected}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Tout sélectionner"
                  className={isSomeSelected ? "opacity-50" : ""}
                  data-testid="select-all-checkbox"
                />
              </TableHead>
              {displayColumns.map((column) => (
                <SortableHeader 
                  key={column.key}
                  column={column.key} 
                  sortColumn={sortColumn} 
                  sortDirection={sortDirection} 
                  onSort={handleSort}
                  className={column.align === 'right' ? 'text-right' : ''}
                >
                  {column.label}
                </SortableHeader>
              ))}
              <TableHead className="w-32">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={displayColumns.length + 2} className="text-center py-8 text-muted-foreground">
                  <FileInput className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucune entrée trouvée</p>
                  {hasActiveFilters && (
                    <Button variant="link" onClick={resetFilters} className="mt-2">
                      Réinitialiser les filtres
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              paginatedEntries.map((entry) => (
                <TableRow 
                  key={entry.id} 
                  data-testid={`entry-row-${entry.id}`}
                  className={selectedIds.has(entry.id) ? "bg-primary/5" : ""}
                >
                  <TableCell>
                    <Checkbox 
                      checked={selectedIds.has(entry.id)}
                      onCheckedChange={() => toggleSelectOne(entry.id)}
                      aria-label={`Sélectionner ${entry.invoice_number}`}
                      data-testid={`select-checkbox-${entry.id}`}
                    />
                  </TableCell>
                  {displayColumns.map((column) => (
                    <TableCell 
                      key={column.key}
                      className={column.align === 'right' ? 'text-right' : ''}
                    >
                      {getCellValue(entry, column.key)}
                    </TableCell>
                  ))}
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {/* File attachment icon - always reserve space for alignment */}
                      {entry.file_id ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openFileWithAuth(entry.file_id, entry.file_name)}
                          title={`Voir la pièce jointe: ${entry.file_name || 'Fichier'}`}
                          className="text-blue-500 hover:text-blue-600"
                          data-testid={`view-file-${entry.id}`}
                        >
                          <Paperclip className="w-4 h-4" />
                        </Button>
                      ) : (
                        <div className="w-8 h-8" />
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openViewEntry(entry)}
                        title="Voir"
                        data-testid={`view-entry-${entry.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {canEdit && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditEntry(entry)}
                            title="Modifier"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDuplicateEntry(entry)}
                            title="Reconduire"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedEntry(entry);
                              setDeleteDialogOpen(true);
                            }}
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {filteredAndSortedEntries.length > 0 && (
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Afficher</span>
            <Select value={itemsPerPage.toString()} onValueChange={(v) => setItemsPerPage(parseInt(v))}>
              <SelectTrigger className="w-[70px] h-8" data-testid="items-per-page">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span>entrées par page</span>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              Page {currentPage} sur {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                data-testid="pagination-first"
              >
                «
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                data-testid="pagination-prev"
              >
                ‹
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                data-testid="pagination-next"
              >
                ›
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                data-testid="pagination-last"
              >
                »
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Entry sidebar */}
      <Sheet
        open={wizardOpen}
        onOpenChange={(open) => {
          setWizardOpen(open);
          if (!open) resetWizard();
        }}
      >
        <SheetContent
          side="right"
          className="flex h-full w-[min(45vw,860px)] min-w-[400px] max-w-full flex-col gap-0 border-l border-border bg-card p-0 sm:max-w-none"
          data-testid="entry-wizard"
        >
          <SheetHeader className="shrink-0 border-b border-border/60 px-6 py-5 pr-14 text-left">
            <SheetTitle>
              {isViewing
                ? 'Détails de l\'entrée'
                : isDuplicating
                  ? 'Reconduire l\'entrée'
                  : isEditing
                    ? 'Modifier l\'entrée'
                    : 'Nouvelle entrée'}
            </SheetTitle>
            <SheetDescription>
              {isViewing
                ? 'Consultez le récapitulatif de l\'entrée'
                : isDuplicating
                  ? 'Dupliquez une entrée existante'
                  : isEditing
                    ? 'Modifiez les informations de l\'entrée'
                    : 'Saisissez une nouvelle facture'}
            </SheetDescription>
          </SheetHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-2">
            <Accordion
              type="single"
              collapsible
              value={entryAccordionOpen}
              onValueChange={setEntryAccordionOpen}
              className="w-full border-0"
            >
              <AccordionItem value="description" className="border-0 border-b border-border/60">
                <AccordionTrigger className="py-4 text-sm font-medium hover:no-underline">
                  Description
                </AccordionTrigger>
                <AccordionContent className="pb-4 pt-0">
                  <div className="space-y-4 rounded-lg border border-border/60 bg-muted/20 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Informations de la facture
                    </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>N° Facture *</Label>
                  <Input
                    value={entryData.invoice_number}
                    onChange={(e) => setEntryData({ ...entryData, invoice_number: e.target.value })}
                    placeholder="FAC-2025-001"
                    data-testid="entry-invoice-number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de facture *</Label>
                  <Input
                    type="date"
                    value={entryData.invoice_date}
                    onChange={(e) => handleInvoiceDateChange(e.target.value)}
                    data-testid="entry-invoice-date"
                  />
                </div>
              </div>

              {/* Currency Section */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Devise</Label>
                  <Select
                    value={entryData.currency}
                    onValueChange={handleCurrencyChange}
                  >
                    <SelectTrigger data-testid="entry-currency">
                      <SelectValue placeholder="Sélectionner une devise" />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {entryData.currency !== 'EUR' && (
                  <div className="space-y-2">
                    <Label>Taux de change ({entryData.currency} → EUR)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.0001"
                        value={entryData.exchange_rate}
                        onChange={(e) => setEntryData({ ...entryData, exchange_rate: parseFloat(e.target.value) || 0 })}
                        className="flex-1"
                        disabled={exchangeRateLoading}
                        data-testid="entry-exchange-rate"
                      />
                      {exchangeRateLoading && (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                      )}
                    </div>
                    {exchangeRateInfo && (
                      <div className="text-xs text-muted-foreground">
                        <span className="font-medium">1 {entryData.currency} = {exchangeRateInfo.rate?.toFixed(4)} EUR</span>
                        <span className="ml-2">({exchangeRateInfo.source})</span>
                        {exchangeRateInfo.warning && (
                          <span className="block text-orange-500">{exchangeRateInfo.warning}</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Fournisseur *</Label>
                <Combobox
                  options={suppliers.map(s => ({ value: s.id, label: s.name }))}
                  value={entryData.supplier_id}
                  onValueChange={(value) => {
                    const supplier = suppliers.find(s => s.id === value);
                    setEntryData({
                      ...entryData,
                      supplier_id: value,
                      supplier_name: supplier?.name || ''
                    });
                  }}
                  placeholder="Sélectionner un fournisseur..."
                  searchPlaceholder="Rechercher..."
                  emptyText="Aucun fournisseur trouvé"
                  data-testid="entry-supplier"
                />
              </div>

              <div className="space-y-2">
                <Label>Destinataire (Société payeuse) *</Label>
                <Combobox
                  options={companies.map(c => ({ value: c.id, label: c.name }))}
                  value={entryData.payer_company_id}
                  onValueChange={(value) => {
                    const company = companies.find(c => c.id === value);
                    setEntryData({
                      ...entryData,
                      payer_company_id: value,
                      payer_company_name: company?.name || ''
                    });
                  }}
                  placeholder="Sélectionner la société qui paye..."
                  searchPlaceholder="Rechercher..."
                  emptyText="Aucune société trouvée"
                  data-testid="entry-payer"
                />
              </div>

              <div className="space-y-2">
                <Label>Pièce jointe (facultatif)</Label>
                {entryData.file_id ? (
                  // File uploaded - show file info
                  <div className="border border-border rounded-lg p-4 flex items-center justify-between bg-muted/30">
                    <div className="flex items-center gap-3">
                      {(() => {
                        const FileIcon = getFileIcon(entryData.file_name);
                        return <FileIcon className="w-8 h-8 text-primary" />;
                      })()}
                      <div>
                        <p className="font-medium text-sm">{entryData.file_name}</p>
                        <p className="text-xs text-muted-foreground">Fichier uploadé</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => openFileWithAuth(entryData.file_id, entryData.file_name)}
                        title="Voir le fichier"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleFileRemove}
                        className="text-red-500 hover:text-red-600"
                        title="Supprimer"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  // No file - show dropzone
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                      dragActive 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-primary/50 hover:bg-muted/30'
                    }`}
                    onDragOver={handleFileDragOver}
                    onDragLeave={handleFileDragLeave}
                    onDrop={handleFileDrop}
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="file-dropzone"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="file-input"
                    />
                    {uploadingFile ? (
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-2"></div>
                        <p className="text-sm text-muted-foreground">Upload en cours...</p>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          {dragActive ? 'Déposez le fichier ici' : 'Glissez un fichier ou cliquez pour parcourir'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          PDF, JPG, PNG (max 10 Mo)
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="lignes" className="border-0 border-b border-border/60">
                <AccordionTrigger className="py-4 text-sm font-medium hover:no-underline">
                  Lignes
                </AccordionTrigger>
                <AccordionContent className="pb-4 pt-0">
                  <div className="space-y-4 rounded-lg border border-border/60 bg-muted/20 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Lignes de facturation
                    </p>
              {entryData.lines.map((line, index) => {
                const isLineCollapsed = !!collapsedLineIds[line.id];
                const isDraggingLine = draggingLineIndex === index;
                const isDropTarget = draggingLineIndex !== null
                  && lineDropIndex === index
                  && draggingLineIndex !== index;
                return (
                <div
                  key={line.id}
                  ref={(el) => {
                    lineRowRefs.current[line.id] = el;
                  }}
                  className={`rounded-lg border p-4 transition-colors ${
                    isDropTarget
                      ? 'border-primary ring-1 ring-primary/40'
                      : isDuplicating && line.reconduire_marked
                        ? 'border-orange-500/50 bg-orange-500/10'
                        : 'border-border'
                  } ${isDraggingLine ? 'invisible' : ''}`}
                >
                  <div
                    className={`flex items-center justify-between ${
                      isDuplicating && isLineCollapsed ? 'cursor-grab' : ''
                    } ${draggingLineIndex !== null && isDuplicating && isLineCollapsed ? 'select-none' : ''}`}
                    onMouseDown={(e) => handleLineDragMouseDown(e, index, line.id)}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      {isDuplicating && (
                        <label
                          className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground"
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={!!line.reconduire_marked}
                            onCheckedChange={(checked) =>
                              updateLine(index, 'reconduire_marked', checked === true)
                            }
                            className="border-orange-500 data-[state=checked]:bg-orange-500 data-[state=checked]:text-white"
                            data-testid={`reconduire-line-${index}`}
                          />
                          reconduire
                        </label>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => toggleLineCollapsed(line.id)}
                        aria-expanded={!isLineCollapsed}
                        aria-label={isLineCollapsed ? `Déplier la ligne ${index + 1}` : `Replier la ligne ${index + 1}`}
                        data-testid={`toggle-line-${index}`}
                      >
                        {isLineCollapsed ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronUp className="h-4 w-4" />
                        )}
                      </Button>
                      <h4 className="font-medium">Ligne {index + 1}</h4>
                      {isLineCollapsed && (
                        <span className="text-sm text-muted-foreground">
                          — {getCollapsedLineSummary(line, entryData.currency)}
                        </span>
                      )}
                      {line.split_tag && (
                        <Badge variant="secondary" className="text-xs">
                          {line.split_tag}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openSplitDialog(index)}
                        className="text-blue-500 hover:text-blue-600"
                        title="Diviser entre sociétés"
                      >
                        <Split className="w-4 h-4 mr-1" />
                        Diviser
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLine(index)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {!isLineCollapsed && (
                  <div className="mt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Catégorie *</Label>
                      <Combobox
                        options={getCategoryOptionsForSelect(categories, line.category_id)}
                        value={line.category_id}
                        onValueChange={(value) => updateLine(index, 'category_id', value)}
                        placeholder="Sélectionner..."
                        searchPlaceholder="Rechercher..."
                        emptyText="Aucune catégorie"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Produit *</Label>
                      <Combobox
                        options={getProductOptionsForSelect(products, categories, {
                          categoryId: line.category_id,
                          selectedProductId: line.product_id,
                        })}
                        value={line.product_id}
                        onValueChange={(value) => updateLine(index, 'product_id', value)}
                        placeholder={line.category_id ? "Sélectionner..." : "Choisir une catégorie"}
                        searchPlaceholder="Rechercher..."
                        emptyText={line.category_id ? "Aucun produit dans cette catégorie" : "Choisir une catégorie"}
                        disabled={!line.category_id}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type *</Label>
                      <Select
                        value={line.entry_type}
                        onValueChange={(value) => updateLine(index, 'entry_type', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="subscription">Abonnement</SelectItem>
                          <SelectItem value="one_off">Ponctuel</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {line.entry_type === 'subscription' && (
                      <div className="space-y-2">
                        <Label>Cycle</Label>
                        <Select
                          value={line.billing_cycle}
                          onValueChange={(value) => updateLine(index, 'billing_cycle', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {BILLING_CYCLES.map(c => (
                              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Société (bénéficiaire)</Label>
                      <Combobox
                        options={companies.map(c => ({ value: c.id, label: c.name }))}
                        value={line.company_id}
                        onValueChange={(value) => updateLine(index, 'company_id', value)}
                        placeholder="Sélectionner..."
                        searchPlaceholder="Rechercher..."
                        emptyText="Aucune société"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Établissement</Label>
                      <Combobox
                        options={getEstablishmentsForCompany(line.company_id).map(e => ({ value: e.id, label: e.name }))}
                        value={line.establishment_id}
                        onValueChange={(value) => updateLine(index, 'establishment_id', value)}
                        placeholder="Sélectionner..."
                        searchPlaceholder="Rechercher..."
                        emptyText={line.company_id ? "Aucun établissement" : "Sélectionnez d'abord une société"}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Secteur</Label>
                      <Combobox
                        options={getSectorsForEstablishment(line.establishment_id, line.company_id).map(s => ({ value: s.id, label: s.name }))}
                        value={line.sector_id}
                        onValueChange={(value) => updateLine(index, 'sector_id', value)}
                        placeholder="Sélectionner..."
                        searchPlaceholder="Rechercher..."
                        emptyText={line.company_id ? "Aucun secteur" : "Sélectionnez d'abord une société"}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Utilisateur</Label>
                      <Combobox
                        options={getUsersForSectorOrCompany(line.sector_id, line.company_id).map(u => ({ 
                          value: u.id, 
                          label: u.display_name || `${u.first_name} ${u.last_name}` 
                        }))}
                        value={line.user_id}
                        onValueChange={(value) => updateLine(index, 'user_id', value)}
                        placeholder="Sélectionner..."
                        searchPlaceholder="Rechercher..."
                        emptyText={line.sector_id ? "Aucun utilisateur pour ce secteur" : line.company_id ? "Aucun utilisateur" : "Sélectionnez d'abord une société"}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Montant HT * {entryData.currency !== 'EUR' && `(${entryData.currency})`}</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={line.amount_ht}
                        onChange={(e) => updateLine(index, 'amount_ht', e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>TVA</Label>
                      <Select
                        value={String(line.vat_rate)}
                        onValueChange={(value) => updateLine(index, 'vat_rate', parseFloat(value))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {VAT_RATES.map(r => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Montant TVA</Label>
                      <Input
                        value={formatCurrency(line.amount_vat, entryData.currency)}
                        disabled
                        className="bg-muted"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Montant TTC</Label>
                      <Input
                        value={formatCurrency(line.amount_ttc, entryData.currency)}
                        disabled
                        className="bg-muted font-medium"
                      />
                    </div>
                  </div>
                  </div>
                  )}
                </div>
              );
              })}

              {lineDragGhost && (() => {
                const ghostLine = entryData.lines[lineDragGhost.index];
                if (!ghostLine) return null;
                const ghostIndex = lineDragGhost.index;
                return (
                  <div
                    className={`pointer-events-none fixed z-[100] rounded-lg border bg-card p-4 shadow-2xl ring-2 ring-primary/70 ${
                      ghostLine.reconduire_marked
                        ? 'border-orange-500/60 bg-orange-500/15'
                        : 'border-primary/80'
                    }`}
                    style={{
                      left: lineDragGhost.x,
                      top: lineDragGhost.y,
                      width: lineDragGhost.width,
                    }}
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      {isDuplicating && (
                        <span
                          className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                            ghostLine.reconduire_marked
                              ? 'border-orange-500 bg-orange-500 text-white'
                              : 'border-orange-500'
                          }`}
                          aria-hidden
                        >
                          {ghostLine.reconduire_marked ? (
                            <Check className="h-3 w-3" />
                          ) : null}
                        </span>
                      )}
                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="shrink-0 font-medium">Ligne {ghostIndex + 1}</span>
                      <span className="truncate text-sm text-muted-foreground">
                        — {getCollapsedLineSummary(ghostLine, entryData.currency)}
                      </span>
                    </div>
                  </div>
                );
              })()}

              <Button
                variant="outline"
                onClick={addLine}
                className="w-full"
                data-testid="add-line-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une ligne
              </Button>

              {entryData.lines.length > 0 && (
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex justify-between text-sm">
                    <span>{entryData.lines.length} ligne(s)</span>
                    <div className="space-x-4">
                      <span>HT: {formatCurrency(totals.total_ht, entryData.currency)}</span>
                      <span>TVA: {formatCurrency(totals.total_vat, entryData.currency)}</span>
                      <span className="font-bold">TTC: {formatCurrency(totals.total_ttc, entryData.currency)}</span>
                      {entryData.currency !== 'EUR' && (
                        <span className="text-muted-foreground ml-2">
                          ({formatCurrency(totals.total_ttc_eur, 'EUR')})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="recapitulatif" className="border-0 border-b border-border/60 last:border-b-0">
                <AccordionTrigger className="py-4 text-sm font-medium hover:no-underline">
                  Récapitulatif
                </AccordionTrigger>
                <AccordionContent className="pb-4 pt-0">
                  <div className="space-y-4 rounded-lg border border-border/60 bg-muted/20 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Récapitulatif et validation
                    </p>
              <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-muted-foreground">N° Facture:</span> <span className="font-medium">{entryData.invoice_number}</span></div>
                  <div><span className="text-muted-foreground">Date:</span> <span className="font-medium">{new Date(entryData.invoice_date).toLocaleDateString('fr-FR')}</span></div>
                  <div><span className="text-muted-foreground">Fournisseur:</span> <span className="font-medium">{entryData.supplier_name}</span></div>
                  <div><span className="text-muted-foreground">Destinataire:</span> <span className="font-medium">{entryData.payer_company_name}</span></div>
                  <div><span className="text-muted-foreground">Devise:</span> <span className="font-medium">{entryData.currency}</span></div>
                  {entryData.currency !== 'EUR' && (
                    <div><span className="text-muted-foreground">Taux:</span> <span className="font-medium">1 {entryData.currency} = {entryData.exchange_rate?.toFixed(4)} EUR</span></div>
                  )}
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Produit</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Société</TableHead>
                    <TableHead>Secteur</TableHead>
                    <TableHead className="text-right">HT</TableHead>
                    <TableHead className="text-right">TTC</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entryData.lines.map((line, index) => (
                    <TableRow key={line.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell className="font-medium">{line.product_name}</TableCell>
                      <TableCell>{line.entry_type === 'subscription' ? 'Abo.' : 'Ponct.'}</TableCell>
                      <TableCell>{line.company_name || '-'}</TableCell>
                      <TableCell>{line.sector_name || '-'}</TableCell>
                      <TableCell className="text-right">{formatCurrency(line.amount_ht, entryData.currency)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(line.amount_ttc, entryData.currency)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="bg-primary/10 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{entryData.lines.length} ligne(s)</span>
                  <div className="text-right space-y-1">
                    <div className="text-sm">Total HT: {formatCurrency(totals.total_ht, entryData.currency)}</div>
                    <div className="text-sm">Total TVA: {formatCurrency(totals.total_vat, entryData.currency)}</div>
                    <div className="text-lg font-bold">Total TTC: {formatCurrency(totals.total_ttc, entryData.currency)}</div>
                    {entryData.currency !== 'EUR' && (
                      <div className="text-sm text-muted-foreground border-t pt-1 mt-1">
                        Équivalent EUR: {formatCurrency(totals.total_ttc_eur, 'EUR')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          <SheetFooter className="mt-auto shrink-0 border-t border-border/60 px-6 py-4 sm:justify-end sm:space-x-2">
            {isViewing ? (
              <Button
                variant="outline"
                onClick={() => {
                  setWizardOpen(false);
                  resetWizard();
                }}
                data-testid="entry-view-close-btn"
              >
                Fermer
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setWizardOpen(false);
                    resetWizard();
                  }}
                >
                  Annuler
                </Button>
                {entryAccordionOpen === 'recapitulatif' && (
                  <Button onClick={submitEntry} data-testid="wizard-submit-btn">
                    <Check className="w-4 h-4 mr-2" />
                    {isEditing ? 'Enregistrer' : isDuplicating ? 'Reconduire' : 'Valider'}
                  </Button>
                )}
              </>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l&apos;entrée ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action supprimera définitivement l&apos;entrée &quot;{selectedEntry?.invoice_number}&quot; et toutes ses lignes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate Dialog */}
      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Voulez-vous vraiment supprimer {selectedIds.size} entrée{selectedIds.size > 1 ? 's' : ''} ?
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="bulk-delete-confirm-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        dataType="entries"
        title={selectedIds.size > 0 
          ? `Exporter ${selectedIds.size} entrée(s) sélectionnée(s)`
          : hasActiveFilters 
            ? `Exporter ${filteredAndSortedEntries.length} entrée(s) filtrée(s)`
            : "Exporter les entrées"
        }
        entryIds={selectedIds.size > 0 
          ? Array.from(selectedIds) 
          : hasActiveFilters 
            ? filteredAndSortedEntries.map(e => e.id)
            : []
        }
      />

      {/* Settings Dialog for Column Preferences */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="bg-card max-w-md" data-testid="settings-dialog">
          <DialogHeader>
            <DialogTitle>Paramètres du tableau</DialogTitle>
            <DialogDescription>
              Sélectionnez les colonnes à afficher et réorganisez-les par glisser-déposer
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Label className="text-sm font-medium mb-3 block">Colonnes visibles</Label>
            <div className="space-y-1 border rounded-lg p-2 bg-muted/30">
              {columnOrder.map((columnKey) => {
                const column = ALL_COLUMNS.find(c => c.key === columnKey);
                if (!column) return null;
                const isVisible = visibleColumns.includes(columnKey);
                const isDragging = draggedColumn === columnKey;
                
                return (
                  <div
                    key={columnKey}
                    draggable
                    onDragStart={(e) => handleDragStart(e, columnKey)}
                    onDragOver={(e) => handleDragOver(e, columnKey)}
                    onDragEnd={handleDragEnd}
                    className={`
                      flex items-center gap-3 p-2 rounded-md cursor-move
                      ${isDragging ? 'bg-primary/20 opacity-50' : 'hover:bg-muted'}
                      ${!isVisible ? 'opacity-50' : ''}
                      transition-all duration-150
                    `}
                    data-testid={`column-item-${columnKey}`}
                  >
                    <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <Checkbox
                      id={`col-${columnKey}`}
                      checked={isVisible}
                      onCheckedChange={() => toggleColumn(columnKey)}
                      disabled={column.required}
                      data-testid={`column-checkbox-${columnKey}`}
                    />
                    <label 
                      htmlFor={`col-${columnKey}`}
                      className="flex-1 text-sm cursor-pointer select-none"
                    >
                      {column.label}
                      {column.required && (
                        <span className="text-xs text-muted-foreground ml-2">(obligatoire)</span>
                      )}
                    </label>
                    {isVisible ? (
                      <Eye className="w-4 h-4 text-green-500" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Glissez les éléments pour réorganiser les colonnes
            </p>
          </div>

          <DialogFooter className="flex justify-between sm:justify-between">
            <Button variant="ghost" onClick={handleResetSettings} data-testid="reset-settings-btn">
              Réinitialiser
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleSaveSettings} data-testid="save-settings-btn">
                Sauvegarder
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split Line Dialog */}
      <Dialog open={splitDialogOpen} onOpenChange={setSplitDialogOpen}>
        <DialogContent className="bg-card max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Split className="w-5 h-5" />
              Diviser cette ligne entre plusieurs sociétés
            </DialogTitle>
            {splitLineIndex !== null && entryData.lines[splitLineIndex] && (
              <DialogDescription className="text-base">
                Ligne source : <strong>{entryData.lines[splitLineIndex].product_name}</strong> - {formatCurrency(entryData.lines[splitLineIndex].amount_ht, entryData.currency)} HT (TVA {entryData.lines[splitLineIndex].vat_rate}%)
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Mode Selection */}
            <div className="bg-muted/30 rounded-lg p-4">
              <Label className="text-sm font-medium mb-3 block">Mode de répartition</Label>
              <RadioGroup 
                value={splitMode} 
                onValueChange={setSplitMode}
                className="flex gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="equal" id="split-equal" />
                  <Label htmlFor="split-equal" className="cursor-pointer">Égal (même montant)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="prorata" id="split-prorata" />
                  <Label htmlFor="split-prorata" className="cursor-pointer">Prorata (pourcentages)</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Company Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Sélectionner les sociétés</Label>
                <span className="text-sm text-muted-foreground">
                  {splitCompanies.length} société(s) sélectionnée(s)
                </span>
              </div>
              <div className="border rounded-lg p-4 max-h-[200px] overflow-y-auto space-y-2">
                {companies.map(company => {
                  const isSelected = splitCompanies.some(c => c.company_id === company.id);
                  const splitCompany = splitCompanies.find(c => c.company_id === company.id);
                  
                  return (
                    <div key={company.id} className="flex items-center gap-4">
                      <div className="flex items-center gap-2 min-w-[200px]">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSplitCompany(company)}
                          id={`split-company-${company.id}`}
                        />
                        <Label 
                          htmlFor={`split-company-${company.id}`} 
                          className="cursor-pointer font-medium"
                        >
                          {company.name}
                        </Label>
                      </div>
                      
                      {splitMode === 'prorata' && isSelected && (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={splitCompany?.percentage || ''}
                            onChange={(e) => updateSplitCompany(company.id, 'percentage', e.target.value)}
                            className="w-24"
                            placeholder="0"
                          />
                          <span className="text-muted-foreground">%</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Prorata Total */}
              {splitMode === 'prorata' && splitCompanies.length > 0 && (
                <div className={`text-right text-sm ${Math.abs(getTotalPercentage() - 100) < 0.01 ? 'text-green-600' : 'text-orange-500'}`}>
                  Total : {getTotalPercentage().toFixed(2)}% 
                  {Math.abs(getTotalPercentage() - 100) >= 0.01 && (
                    <span className="ml-2">⚠️ Le total doit être égal à 100%</span>
                  )}
                </div>
              )}
            </div>

            {/* Preview Table */}
            {splitCompanies.length >= 2 && (
              <div className="space-y-3">
                <Label className="text-sm font-medium">Aperçu des lignes à créer ({splitCompanies.length} lignes)</Label>
                <div className="border rounded-lg overflow-hidden overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="w-8">#</TableHead>
                        <TableHead>Société</TableHead>
                        <TableHead>Établissement</TableHead>
                        <TableHead>Secteur</TableHead>
                        <TableHead>Utilisateur</TableHead>
                        {splitMode === 'prorata' && <TableHead className="w-16 text-center">%</TableHead>}
                        <TableHead className="text-right">Montant HT</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getSplitPreview().map((preview, index) => (
                        <TableRow key={preview.company_id}>
                          <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                          <TableCell className="font-medium">{preview.company_name}</TableCell>
                          <TableCell>
                            <Combobox
                              options={getEstablishmentsForCompany(preview.company_id).map(e => ({ value: e.id, label: e.name }))}
                              value={preview.establishment_id}
                              onValueChange={(value) => updateSplitCompany(preview.company_id, 'establishment_id', value)}
                              placeholder="Choisir..."
                              searchPlaceholder="Rechercher..."
                              emptyText="Aucun établissement"
                              className="w-full min-w-[140px]"
                            />
                          </TableCell>
                          <TableCell>
                            <Combobox
                              options={getSectorsForEstablishment(preview.establishment_id, preview.company_id).map(s => ({ value: s.id, label: s.name }))}
                              value={preview.sector_id}
                              onValueChange={(value) => updateSplitCompany(preview.company_id, 'sector_id', value)}
                              placeholder="Choisir..."
                              searchPlaceholder="Rechercher..."
                              emptyText="Aucun secteur"
                              className="w-full min-w-[140px]"
                            />
                          </TableCell>
                          <TableCell>
                            <Combobox
                              options={getUsersForSectorOrCompany(preview.sector_id, preview.company_id).map(u => ({ 
                                value: u.id, 
                                label: u.display_name || `${u.first_name} ${u.last_name}` 
                              }))}
                              value={preview.user_id}
                              onValueChange={(value) => updateSplitCompany(preview.company_id, 'user_id', value)}
                              placeholder="Choisir..."
                              searchPlaceholder="Rechercher..."
                              emptyText={preview.sector_id ? "Aucun utilisateur" : "Sélectionnez un secteur"}
                              className="w-full min-w-[140px]"
                            />
                          </TableCell>
                          {splitMode === 'prorata' && (
                            <TableCell className="text-center font-medium">{preview.displayTag}</TableCell>
                          )}
                          <TableCell className="text-right font-medium">
                            {formatCurrency(preview.amount_ht, entryData.currency)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Summary */}
                <div className="flex justify-end">
                  <div className="text-right text-sm bg-muted/30 px-4 py-2 rounded-lg">
                    <span className="text-muted-foreground">Total : </span>
                    <span className="font-bold">
                      {formatCurrency(
                        getSplitPreview().reduce((sum, p) => sum + p.amount_ht, 0),
                        entryData.currency
                      )}
                    </span>
                    {splitLineIndex !== null && entryData.lines[splitLineIndex] && (
                      <span className={`ml-2 ${
                        Math.abs(getSplitPreview().reduce((sum, p) => sum + p.amount_ht, 0) - parseFloat(entryData.lines[splitLineIndex].amount_ht)) < 0.01
                          ? 'text-green-600'
                          : 'text-orange-500'
                      }`}>
                        ✓
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {splitCompanies.length < 2 && (
              <div className="text-center text-muted-foreground py-8 bg-muted/20 rounded-lg">
                Sélectionnez au moins 2 sociétés pour diviser cette ligne
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSplitDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={executeSplit} 
              disabled={!canExecuteSplit()}
              data-testid="split-confirm-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Créer {splitCompanies.length} lignes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Entries;
